<template>
  <div>
    <RevLoadingScreen
      v-if="isSubmitting"
      class="mt-20 size-full self-center"
      :text="i18n(translations.updatingText)"
    />
    <template v-if="!isSubmitting">
      <RevInfoBlock
        class="mb-12"
        :content="i18n(translations.modalInfoBlockContent)"
        :icon="IconLightbulb"
        :title="i18n(translations.modalInfoBlockTitle)"
        variant="warning"
      />
      <RevInfoBlock
        v-if="apiError"
        class="mb-16"
        :icon="IconWarning"
        :title="i18n(translations.errorTitle)"
        variant="danger"
      >
        {{ apiError }}
      </RevInfoBlock>
      <RevForm :id="FORM_ID" :initial-values :validate @submit="handleSubmit">
        <template #default="{ values, errors }">
          <RevInputText
            id="legalCompanyName"
            v-model="values.legalCompanyName"
            class="mb-8"
            :error="errors.legalCompanyName"
            :label="i18n(translations.legalBusinessNameInputLabel)"
            type="text"
          />
          <RevInputText
            v-if="shouldDisplayCompanyIdNumberInput"
            id="companyIdentificationNumber"
            v-model="values.companyIdentificationNumber"
            class="mb-8"
            :description="companyIdNumberInputDescription"
            :disabled="shouldDisableCompanyIdNumberInput"
            :error="errors.companyIdentificationNumber"
            :label="i18n(translations.companyIdentificationNumberInputLabel)"
            type="text"
          />
        </template>
      </RevForm>
    </template>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import type { GetProfileViewLegalDetails } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevForm, makeValidate, maxLength, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { IconWarning } from '@ds/icons/IconWarning'

import { FORM_IDS } from '~/scopes/profile/pages/Profile.constants'

import translations from '../../OverviewModal.translations'

import {
  MAX_COMPANY_IDENTIFICATION_NUMBER_LENGTH,
  MAX_LEGAL_COMPANY_NAME_LENGTH,
} from './DefaultForm.constants'
import type { FormSellerLegalDetailsValues } from './DefaultForm.types'

const FORM_ID = FORM_IDS.EDIT_LEGAL_DETAILS.default

const props = defineProps<{
  legalDetails: GetProfileViewLegalDetails | null
}>()

const emit = defineEmits(['update-form', 'close-modal'])

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref('')
const isSubmitting = ref(false)

const initialValues = computed<FormSellerLegalDetailsValues>(() => ({
  legalCompanyName: '',
  companyIdentificationNumber: '',
  ...props.legalDetails,
}))

const shouldDisableCompanyIdNumberInput = computed(
  () => !!props.legalDetails?.companyIdentificationNumber,
)

// TODO might be useless. Based on the DevPortal, this API field is not optional
const shouldDisplayCompanyIdNumberInput = computed(
  () =>
    props.legalDetails && 'companyIdentificationNumber' in props.legalDetails,
)

const companyIdNumberInputDescription = computed(() =>
  shouldDisableCompanyIdNumberInput.value
    ? i18n(translations.labelDisabledInput)
    : '',
)

const validate = makeValidate<FormSellerLegalDetailsValues>({
  legalCompanyName: [
    required(i18n(translations.required)),
    maxLength(MAX_LEGAL_COMPANY_NAME_LENGTH, i18n(translations.invalidLength)),
  ],
  companyIdentificationNumber: [
    maxLength(
      MAX_COMPANY_IDENTIFICATION_NUMBER_LENGTH,
      i18n(translations.invalidLength),
    ),
  ],
})

async function handleSubmit({
  companyIdentificationNumber,
  legalCompanyName,
}: FormSellerLegalDetailsValues) {
  apiError.value = ''
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: {
        legalCompanyName,
        ...(!shouldDisableCompanyIdNumberInput.value &&
        companyIdentificationNumber
          ? { companyIdentificationNumber }
          : null),
      },
    })
    openSuccessToast({
      title: i18n(translations.legalDetailsUpdatedToastSuccessTitle),
      content: i18n(translations.legalDetailsUpdatedToastSuccessContent),
    })
    emit('update-form')
    emit('close-modal')
  } catch (error) {
    apiError.value = getApiErrorMessage(error as unknown as Error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
